import { useFieldsToDisplay } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay/atom';
import React from 'react';
import 'twin.macro';
import { format } from 'utils/time/format';
import { useDeleteCJSeancesBetweenDates } from 'containers/CahierJournal/queries/useDeleteCJSeancesBetweenDates';
import { Menu } from 'components/Menu';
import useNotification from 'containers/App/Notifications/useNotification';
import { PDFIcon } from 'components/Icons/icons/PDF';
import {
  ArrowDownOnSquareStackIcon,
  ArrowDownTrayIcon,
  ArrowUpOnSquareStackIcon,
  Bars4Icon,
  BarsArrowUpIcon,
  CalendarIcon as Calendar,
  CheckCircleIcon as CheckCircle,
  PencilSquareIcon,
  TrashIcon as Trash,
} from '@heroicons/react/24/outline';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { doesDayBelongToWorkingDays } from 'containers/CahierJournal/utils/doesDayBelongToWorkingDays';
import { useSetCJSeanceStatus } from 'containers/CahierJournal/queries/useSetCJSeanceStatus';
import { useNavigate } from 'react-router-dom';
import { CopySuccessMessage } from 'containers/CahierJournal/CopySuccessMessage';
import { useOpenCopyEventsToAnotherDayModal } from 'containers/CahierJournal/CopyEventsToAnotherDayModal/atom';
import useReunionModal from 'containers/Planner/Main/ReunionModal/useReunionModal';
import { setHoursAndMinutes } from 'utils/time/setHoursAndMinutes';
import setMinutes from 'utils/time/setMinutes';
import startOfMinute from 'utils/time/startOfMinute';
import setHours from 'utils/time/setHours';
import { useUser } from 'containers/App/context/useUser';
import { useOpenCreateCJSeanceModal } from 'containers/CahierJournal/CreateCJSeanceModal/useOpenCreateCJSeanceModal';
import { useOpenCopyEventsFromEDTModal } from 'containers/CahierJournal/CopyEventsFromEDTModal/useOpenCopyEventsFromEDTModal';
import { useTranslation } from 'react-i18next';
import FakeUserProvider from 'containers/App/context/FakeUserProvider';
import { emptySeance } from 'containers/CahierJournal/CreateCJSeanceModal/emptySeance';
import { useDownloadSexyCJ } from 'containers/CahierJournal/queries/useDownloadSexyCJ';

interface Props {
  day: Date;
  view: string;
  previewLink: string;
}

export function SingleDayMenuItems(props: Props) {
  const {
    fonts: { cj: settings },
  } = useUser();
  const { download: downloadSexy } = useDownloadSexyCJ();
  const navigate = useNavigate();
  const { locale, guestMode } = useUser();
  const canModify = !guestMode?.enabled || guestMode?.permission === 'write';
  const { notifySuccess } = useNotification();
  const classrooms = useClassrooms();
  const fields = useFieldsToDisplay();
  const canMarkAllAsDone = fields.includes('status');
  const { day, view } = props;
  const user = useUser();
  const classroomsOfTheDay = classrooms.filter(c =>
    doesDayBelongToWorkingDays(day, c.workingDays),
  );
  const openCopyEventsToAnotherDayModal = useOpenCopyEventsToAnotherDayModal();
  const openCreateCJSeanceModal = useOpenCreateCJSeanceModal();

  const { openNewReunionModal } = useReunionModal();
  const openCopyEventsFromEDTModal = useOpenCopyEventsFromEDTModal();
  const deleteEvents = useDeleteCJSeancesBetweenDates();
  const updateCJSeancesStatus = useSetCJSeanceStatus();
  const { t } = useTranslation(['cahierJournal', 'common']);

  const downloadCJ = () =>
    downloadSexy({
      day: format(day, 'fr-FR'),
      view: 'day',
      direction: 'next',
      settings,
      lng: user.locale,
      tz: user.location,
    });

  return (
    <>
      <div tw="py-1">
        <Menu.Item
          onClick={() => {
            navigate(props.previewLink);
          }}
          icon={PDFIcon as any}
          text={t('common:buttons.pdfPreview')}
        />
        <Menu.Item
          onClick={() => {
            downloadCJ();
          }}
          icon={ArrowDownTrayIcon}
          text={t('common:buttons.downloadPdf')}
        />
      </div>
      {canModify && (
        <div tw="py-1">
          <Menu.Item
            onClick={() => {
              openCreateCJSeanceModal({
                classrooms,
                seance: emptySeance({
                  start: startOfMinute(setHours(day, 8)),
                  end: startOfMinute(setHours(day, 9)),
                  classrooms,
                  type: 'classic',
                }),
              });
            }}
            icon={Bars4Icon}
            text={t('cahierJournal:actions.addSeance')}
          />
          <Menu.Item
            onClick={() => {
              openCreateCJSeanceModal({
                classrooms,
                seance: emptySeance({
                  start: startOfMinute(setHours(day, 8)),
                  end: startOfMinute(setHours(day, 9)),
                  classrooms,
                  type: 'rappel',
                  title: t('cahierJournal:labels.rappelDefaultTitle'),
                }),
              });
            }}
            icon={BarsArrowUpIcon}
            text={t('cahierJournal:actions.addRappel')}
          />
          <Menu.Item
            onClick={() => {
              openCreateCJSeanceModal({
                classrooms,
                seance: emptySeance({
                  start: startOfMinute(setHours(day, 8)),
                  end: startOfMinute(setHours(day, 9)),
                  classrooms,
                  type: 'bilan',
                  title: t('cahierJournal:labels.bilanDefaultTitle'),
                }),
              });
            }}
            icon={BarsArrowUpIcon}
            tw="[&>svg]:-scale-y-100"
            text={t('cahierJournal:actions.addBilan')}
          />
          {!guestMode?.enabled && (
            <Menu.Item
              onClick={() =>
                openNewReunionModal(
                  null,
                  setMinutes(setHoursAndMinutes(day, new Date()), 0),
                )
              }
              icon={PencilSquareIcon}
              text={t('cahierJournal:dayHeader.addPlannerEvent')}
            />
          )}
        </div>
      )}
      {canModify && (
        <div tw="py-1">
          <Menu.Item
            onClick={() =>
              openCopyEventsToAnotherDayModal({
                to: day,
                classrooms: classroomsOfTheDay,
                onSuccess: ({ to, destSchool, destSchoolyear, eventIds }) => {
                  notifySuccess({
                    title: t('common:messages.congratulations'),
                    layout: 'block',
                    message: (
                      <FakeUserProvider user={user}>
                        <CopySuccessMessage
                          locale={locale}
                          nbOfSpecificEventIdsSelected={eventIds}
                          targetDate={to}
                          school={destSchool}
                          schoolyear={destSchoolyear}
                          view={view}
                        />
                      </FakeUserProvider>
                    ),
                  });
                },
              })
            }
            icon={ArrowDownOnSquareStackIcon}
            text={t('cahierJournal:actions.getSeancesFromAnotherDay')}
          />
          {!guestMode?.enabled && (
            <Menu.Item
              onClick={() => openCopyEventsFromEDTModal({ date: day })}
              icon={Calendar}
              text={t('cahierJournal:actions.getSeancesFromEDT')}
            />
          )}
          <Menu.Item
            onClick={() =>
              openCopyEventsToAnotherDayModal({
                from: day,
                classrooms: classroomsOfTheDay,
                onSuccess: ({ to, destSchool, destSchoolyear, eventIds }) => {
                  notifySuccess({
                    title: t('common:messages.congratulations'),
                    layout: 'block',
                    message: (
                      <FakeUserProvider user={user}>
                        <CopySuccessMessage
                          locale={locale}
                          nbOfSpecificEventIdsSelected={eventIds}
                          targetDate={to}
                          school={destSchool}
                          schoolyear={destSchoolyear}
                          view={view}
                        />
                      </FakeUserProvider>
                    ),
                  });
                },
              })
            }
            text={t('cahierJournal:dayHeader.copyDayOn')}
            icon={ArrowUpOnSquareStackIcon}
          />
        </div>
      )}
      {canModify && canMarkAllAsDone && (
        <div tw="py-1">
          <Menu.Item
            onClick={() =>
              updateCJSeancesStatus({
                status: 'done',
                date: day,
              })
            }
            tw="whitespace-nowrap"
            icon={CheckCircle}
            text={t('cahierJournal:dayHeader.markAllAsDone')}
          />
        </div>
      )}
      {canModify && (
        <div tw="py-1">
          <Menu.Item
            intent="danger"
            icon={Trash}
            onClick={() => deleteEvents({ from: day, to: day })}
            text={t('cahierJournal:dayHeader.deleteAll')}
          />
        </div>
      )}
    </>
  );
}
